import { useSafeExecution } from "@ckweb/ckfetch/composables";
import { useGetUser } from "../composables/user";
import type { AuthAccount, AuthLoginOptions, PROVIDER_NAMES, UserModel } from "../types";

export const useAuthStore = defineStore("auth", () => {
  const accounts = ref<AuthAccount[]>([]);
  const isAuthed = ref(false);

  const $nuxt = useNuxtApp();
  const { handleSafeExecution } = useSafeExecution();
  const getUserFetch = useGetUser();
  const isReady = useIsReady(getUserFetch);

  const provider = computed({
    get: () => $nuxt.$auth.provider,
    set: (value) => {
      $nuxt.$auth.provider = value;
    },
  });

  const currentAccount = computed(() => {
    if (!accounts.value || accounts.value.length === 0) return;

    return accounts.value[0] as AuthAccount;
  });

  const currentAuthFlow = computed(() => $nuxt.$auth.authFlow);

  const currentAccountUsername = computed(() => {
    if (!currentAccount.value) return "";

    const acc = currentAccount.value;
    const accName = acc?.name ?? "";
    const accUsername = acc?.username;

    if (accName?.length > 0) return accName;

    if (accUsername?.length > 0) return accUsername;

    return "";
  });

  const currentAccountAvatarInitial = computed(() => {
    if (!currentAccount.value) return "U";

    const acc = currentAccount.value;
    const accName = acc?.name ?? "";
    const accUsername = acc?.username;

    if (accName?.length > 0) return accName[0];

    if (accUsername?.length > 0) return accUsername[0];

    return "U";
  });

  const currentUser = computed(() => unref(getUserFetch.data.value) ?? undefined);
  const isLoadingUser = computed(() => getUserFetch.pending.value);
  const isUserResolved = computed(() => !getUserFetch.resolved);

  async function initialize(providerName?: PROVIDER_NAMES) {
    if (providerName) provider.value = providerName;
    await $nuxt.$auth.initialize();
    await refresh();
  }

  async function getAccounts(): Promise<AuthAccount[]> {
    accounts.value = await $nuxt.$auth.getAccounts();
    return accounts.value;
  }

  async function resetAuthFlow() {
    await $nuxt.$auth.resetAuthFlow();
  }

  async function login(options?: AuthLoginOptions) {
    await handleSafeExecution(
      async () => {
        await $nuxt.$auth.login(options);
        await refresh();
      },
      {
        enableGlobalNotifications: true,
        executionName: "initialize login",
      },
    );

    return currentAccount.value;
  }

  async function logout() {
    try {
      await $nuxt.$auth.logout();
      await refresh();
    } catch {
      devConsole.error("🔒 ckAuth: 🔒 Auth logout failed ");
    }
  }

  async function resetPassword(payload: { token: string; newPassword: string }) {
    return await handleSafeExecution(() => $nuxt.$auth.resetPassword(payload), {
      showGlobalLoadingIndicator: true,
      enableGlobalNotifications: true,
      executionName: "reset password",
    });
  }

  async function refresh() {
    await $nuxt.$auth.isReady();
    await getToken();
    await getAccounts();
    isAuthed.value = await $nuxt.$auth.isAuthed();

    return accounts.value;
  }

  async function getToken(): Promise<string | undefined> {
    if (!isAuthed.value) return;
    return $nuxt.$auth.getToken();
  }

  async function refreshToken(givenToken?: string): Promise<string | undefined> {
    return $nuxt.$auth.refreshToken(givenToken);
  }

  async function clearData() {
    await $nuxt.$auth.clearData();
  }

  async function handleAuthError(error: Error) {
    return $nuxt.$auth.handleAuthError(error);
  }

  async function awaitUserLoaded(fn?: (value?: UserModel) => unknown): Promise<UserModel | undefined> {
    if (!isAuthed.value || currentUser.value) {
      return Promise.resolve(currentUser.value).then((v) => {
        fn?.(v);
        return v;
      });
    }

    return new Promise((resolve) => {
      const stop = watch(
        currentUser,
        (value) => {
          if (value) {
            stop();
            resolve(value);
            fn?.(value);
          }
        },
        { flush: "sync" },
      );
    });
  }

  async function refreshCurrentUser() {
    return await getUserFetch.safeFetch();
  }

  function resetCurrentUser() {
    return getUserFetch.reset();
  }

  return {
    currentAccount,
    currentAccountUsername,
    currentAccountAvatarInitial,
    provider,
    currentAuthFlow: readonly(currentAuthFlow),
    accounts: readonly(accounts),
    isAuthed: readonly(isAuthed),
    currentUser,
    isLoadingUser,
    isUserResolved,
    isReady,
    initialize,
    getAccounts,
    login,
    logout,
    resetPassword,
    refresh,
    resetAuthFlow,
    getToken,
    refreshToken,
    clearData,
    handleAuthError,
    awaitUserLoaded,
    refreshCurrentUser,
    resetCurrentUser,
  };
});
